import { useNavigate } from "react-router";
import { ChevronDown, LogOut } from "lucide-react";
import {
  VButton,
  VDropdownMenu,
  VDropdownMenuContent,
  VDropdownMenuItem,
  VDropdownMenuLabel,
  VDropdownMenuSeparator,
  VDropdownMenuTrigger,
  cn,
} from "@/components/ui";
import { SEED_USERS } from "@/domains/auth/mocks/users";
import {
  useAuthSession,
  useCurrentUser,
  useSetCurrentUser,
} from "@/domains/auth/hooks/useCurrentUser";
import { ROLE_ICONS, ROLE_LABELS } from "@/domains/auth/components/roleLabels";

export function RoleSwitcher(): React.JSX.Element {
  const current = useCurrentUser();
  const setUser = useSetCurrentUser();
  const session = useAuthSession();
  const navigate = useNavigate();
  const Icon = ROLE_ICONS[current.role];

  const handleLogout = (): void => {
    session.logout();
    navigate("/login", { replace: true });
  };

  return (
    <VDropdownMenu>
      <VDropdownMenuTrigger asChild>
        <VButton
          variant="ghost"
          size="md"
          className="h-auto gap-2 rounded-md bg-primary-500/30 px-2.5 py-1.5 text-on-primary hover:bg-primary-500/50"
        >
          <span
            className={cn(
              "flex size-7 items-center justify-center rounded-full text-xs font-bold text-white",
              current.avatarColor,
            )}
          >
            {current.firstName[0]}
            {current.lastName[0]}
          </span>
          <span className="hidden text-left md:inline">
            <span className="block font-medium leading-tight">
              {current.firstName} {current.lastName}
            </span>
            <span className="flex items-center gap-1 text-xs text-primary-100">
              <Icon className="size-3" aria-hidden />
              {ROLE_LABELS[current.role]}
            </span>
          </span>
          <ChevronDown className="size-4 text-primary-100" aria-hidden />
        </VButton>
      </VDropdownMenuTrigger>
      <VDropdownMenuContent align="end" className="min-w-[16rem]">
        <VDropdownMenuLabel>Wissel van rol (demo)</VDropdownMenuLabel>
        <VDropdownMenuSeparator />
        {SEED_USERS.map((u) => {
          const ItemIcon = ROLE_ICONS[u.role];
          return (
            <VDropdownMenuItem
              key={u.id}
              onSelect={() => setUser(u)}
              className={cn("gap-3", u.id === current.id && "bg-muted")}
            >
              <span
                className={cn(
                  "flex size-8 items-center justify-center rounded-full text-xs font-bold text-white",
                  u.avatarColor,
                )}
              >
                {u.firstName[0]}
                {u.lastName[0]}
              </span>
              <span className="flex-1">
                <span className="block font-medium text-foreground">
                  {u.firstName} {u.lastName}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <ItemIcon className="size-3" aria-hidden />
                  {ROLE_LABELS[u.role]} — {u.service}
                </span>
              </span>
            </VDropdownMenuItem>
          );
        })}
        <VDropdownMenuSeparator />
        <VDropdownMenuItem onSelect={handleLogout} className="gap-3">
          <LogOut className="size-4 text-muted-foreground" aria-hidden />
          Uitloggen
        </VDropdownMenuItem>
      </VDropdownMenuContent>
    </VDropdownMenu>
  );
}
